/* Shop dropdown: the chevron button opens it for keyboard and touch; hover is CSS. */
(() => {
  document.querySelectorAll('[data-shamma-mega]').forEach((item) => {
    const toggle = item.querySelector('.shamma-desktop-nav__toggle');
    if (!toggle) return;
    const setOpen = (open) => {
      item.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', String(open));
    };
    toggle.addEventListener('click', () => setOpen(!item.classList.contains('is-open')));
    item.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && item.classList.contains('is-open')) {
        setOpen(false);
        toggle.focus();
      }
    });
    item.addEventListener('focusout', (event) => {
      if (!item.contains(event.relatedTarget)) setOpen(false);
    });
    document.addEventListener('click', (event) => {
      if (!item.contains(event.target)) setOpen(false);
    });
  });
})();

/* Pinned header: fade the transparent hero header to white over the first 120px of
   scroll past its natural position (below the announcement bar). */
(() => {
  const root = document.documentElement;
  const naturalTop = () => {
    const bar = document.querySelector('.announcement-bar');
    return bar ? bar.offsetTop + bar.offsetHeight : 0;
  };
  let start = naturalTop();
  let queued = false;
  const update = () => {
    queued = false;
    const fade = Math.min(1, Math.max(0, (window.scrollY - start) / 120));
    root.style.setProperty('--sh-header-fade', fade.toFixed(3));
    root.classList.toggle('sh-header-solid', fade >= 0.5);
  };
  window.addEventListener('scroll', () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(update);
  }, { passive: true });
  window.addEventListener('resize', () => {
    start = naturalTop();
    update();
  });
  update();
})();

/* Plain-text email addresses in page copy (Returns, FAQ answers) become mailto links. */
(() => {
  const scopes = document.querySelectorAll('.sh-page .rte, .sh-support__answer, .sh-support__article .rte');
  const pattern = /([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/gi;
  scopes.forEach((scope) => {
    const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) {
      const node = walker.currentNode;
      if (!node.parentElement.closest('a') && pattern.test(node.textContent)) nodes.push(node);
      pattern.lastIndex = 0;
    }
    nodes.forEach((node) => {
      const fragment = document.createDocumentFragment();
      node.textContent.split(pattern).forEach((part, index) => {
        if (index % 2) {
          const link = document.createElement('a');
          link.href = `mailto:${part}`;
          link.textContent = part;
          fragment.append(link);
        } else if (part) {
          fragment.append(part);
        }
      });
      node.replaceWith(fragment);
    });
  });
})();

/* Fit guide FAQ: after an "FAQ" heading, each bold-only question paragraph and the answer
   paragraphs that follow it become a collapsible <details> item. Metafield copy unchanged. */
(() => {
  document.querySelectorAll('.fit-guide__content').forEach((content) => {
    let heading = [...content.querySelectorAll('h2, h3, h4, h5')].find((h) => /faq/i.test(h.textContent));
    if (!heading) return;
    // The FAQ heading follows the H3 "Step-by-step instructions", so it is an H3 too.
    if (heading.tagName !== 'H3') {
      const h3 = document.createElement('h3');
      h3.className = heading.className;
      h3.append(...heading.childNodes);
      heading.replaceWith(h3);
      heading = h3;
    }
    const isQuestion = (el) => el && el.tagName === 'P' && el.children.length === 1 && el.firstElementChild.tagName === 'STRONG' && el.textContent.trim() === el.firstElementChild.textContent.trim();
    let node = heading.nextElementSibling;
    while (node) {
      if (!isQuestion(node)) { node = node.nextElementSibling; continue; }
      const details = document.createElement('details');
      details.className = 'fit-guide__faq-item';
      const summary = document.createElement('summary');
      summary.textContent = node.textContent.trim();
      details.append(summary);
      let answer = node.nextElementSibling;
      while (answer && !isQuestion(answer) && !/^H[1-6]$/.test(answer.tagName)) {
        const next = answer.nextElementSibling;
        details.append(answer);
        answer = next;
      }
      node.replaceWith(details);
      node = answer;
    }
  });
})();

/* Older blog posts: body copy typed as headings. Long heading-tagged text becomes a paragraph,
   and a second H1 inside the article becomes an H2, so each post has one H1 and a sane outline. */
(() => {
  const content = document.querySelector('.f-article-template__content');
  if (!content) return;
  const retag = (el, tag) => {
    const next = document.createElement(tag);
    [...el.attributes].forEach((a) => next.setAttribute(a.name, a.value));
    next.append(...el.childNodes);
    el.replaceWith(next);
    return next;
  };
  content.querySelectorAll('h1, h2, h3, h4').forEach((el) => {
    const text = el.textContent.trim();
    if (!text) return;
    if (text.length > 90 && !el.querySelector('a:only-child')) retag(el, 'p');
    else if (el.tagName === 'H1') retag(el, 'h2');
  });
})();

/* Keyboard hygiene (design review 3):
   - product gallery: only the visible slide's "open media" button is tabbable, and the
     carousel container is not a tab stop of its own;
   - collapsible sections: triggers report aria-expanded from the open attribute;
   - mobile filter drawer: lacing chips follow their visual order. */
(() => {
  const list = document.querySelector('.f-product__media-list');
  if (list) {
    const syncGallery = () => {
      if (list.getAttribute('tabindex') === '0') list.setAttribute('tabindex', '-1');
      list.querySelectorAll('.f-product__modal-toggle').forEach((toggle) => {
        const cell = toggle.closest('.f-product__media-item, [class*="media-item"], li, .flickity-cell') || toggle.parentElement;
        const visible = !cell.closest('[aria-hidden="true"]') && (cell.classList.contains('is-selected') || !list.classList.contains('flickity-enabled'));
        const next = -1; // zoom is mouse-only; keyboard users use the gallery arrows
        void visible;
        if (toggle.tabIndex !== next) toggle.tabIndex = next;
      });
    };
    // Watch slide state only (not tabindex, which this function writes) to avoid a feedback loop.
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; syncGallery(); });
    }).observe(list, { attributes: true, subtree: true, attributeFilter: ['class', 'aria-hidden'] });
    syncGallery();
  }
  const syncTab = (tab) => {
    const trigger = tab.querySelector(':scope > [data-trigger], :scope > button');
    const content = tab.querySelector(':scope > [data-content]');
    const open = tab.hasAttribute('open') && tab.getAttribute('open') !== 'false';
    if (trigger) {
      trigger.setAttribute('aria-expanded', String(open));
      if (content) {
        if (!content.id) content.id = `sh-collapsible-${Math.random().toString(36).slice(2, 8)}`;
        trigger.setAttribute('aria-controls', content.id);
      }
    }
  };
  document.querySelectorAll('collapsible-tab').forEach((tab) => {
    syncTab(tab);
    new MutationObserver(() => syncTab(tab)).observe(tab, { attributes: true, attributeFilter: ['open'] });
  });
  document.querySelectorAll('.variant-grid__filterset--lacing-system .variant-grid__filters').forEach((group) => {
    const chips = [...group.children];
    chips
      .map((chip, index) => ({ chip, index, order: parseInt(getComputedStyle(chip).order, 10) || 0 }))
      .sort((a, b) => a.order - b.order || a.index - b.index)
      .forEach(({ chip }) => { chip.style.order = '0'; group.append(chip); });
  });
})();

/* Review 4 accessibility:
   - gallery zoom buttons are mouse-only (the lightbox has no keyboard focus management);
   - long product-description subheads become H2 when the page has none;
   - desktop filter sidebar is not announced as a modal dialog;
   - store locator: after a search, the results come back into view, the count is announced and
     country links without results hide;
   - fit guide: keyboard tab switches update ?model= like clicks do. */
(() => {
  document.querySelectorAll('.f-product__modal-toggle').forEach((toggle) => { toggle.tabIndex = -1; });
  const description = document.querySelector('.sh-product .f-product__description');
  if (description) {
    description.querySelectorAll('h3').forEach((h3) => {
      const h2 = document.createElement('h2');
      h2.className = h3.className;
      h2.append(...h3.childNodes);
      h3.replaceWith(h2);
    });
  }
  const sidebar = document.querySelector('.variant-grid__sidebar');
  if (sidebar) {
    const desktop = window.matchMedia('(min-width: 1024px)');
    const syncRole = () => {
      if (desktop.matches) { sidebar.removeAttribute('role'); sidebar.removeAttribute('aria-modal'); }
      else { sidebar.setAttribute('role', 'dialog'); sidebar.setAttribute('aria-modal', 'true'); }
    };
    syncRole();
    desktop.addEventListener('change', syncRole);
  }
  const locator = document.querySelector('.store-locator__panel');
  if (locator) {
    const input = locator.querySelector('.store-locator__search-input');
    const status = document.createElement('p');
    status.className = 'visually-hidden';
    status.setAttribute('role', 'status');
    locator.append(status);
    input && input.addEventListener('input', () => requestAnimationFrame(() => requestAnimationFrame(() => {
      const rows = [...locator.querySelectorAll('.store-locator__row')].filter((r) => !r.hidden && r.style.display !== 'none');
      status.textContent = input.value.trim() ? `${rows.length} location${rows.length === 1 ? '' : 's'} found` : '';
      const countries = new Set(rows.map((r) => { let n = r.previousElementSibling; while (n && !n.matches('[data-country-heading]')) n = n.previousElementSibling; return n && n.id; }));
      locator.querySelectorAll('.store-locator__countries a').forEach((a) => { a.hidden = input.value.trim() && !countries.has(a.getAttribute('href').slice(1)); });
      const top = locator.getBoundingClientRect().top;
      if (top < 0) locator.scrollIntoView({ block: 'start' });
    })));
  }
  document.querySelectorAll('.fit-guide__tabs').forEach((tabs) => {
    tabs.addEventListener('keyup', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      const active = tabs.querySelector('[aria-selected="true"]');
      if (!active) return;
      const url = new URL(window.location);
      url.searchParams.set('model', active.dataset.hash);
      history.replaceState(null, '', url);
    });
  });
})();

/* Product options in keyboard order: the size fieldset is shown after sole and footbed with CSS
   order, so it is also moved there in the DOM. Each fieldset keeps its original position in
   data-sh-pos, which variants-picker.js uses to send options in Shopify's order. */
(() => {
  const arrange = () => {
    document.querySelectorAll('variant-picker').forEach((picker) => {
      const fieldsets = [...picker.querySelectorAll(':scope > fieldset')];
      if (fieldsets.length < 2) return;
      fieldsets.forEach((fieldset, index) => { if (!fieldset.dataset.shPos) fieldset.dataset.shPos = String(index); });
      const size = fieldsets.find((f) => (f.dataset.option || '').includes('size'));
      const last = fieldsets[fieldsets.length - 1];
      if (size && last !== size) last.after(size);
    });
  };
  const root = document.querySelector('product-info');
  if (!root) return;
  arrange();
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; arrange(); });
  }).observe(root, { childList: true, subtree: true });
})();
